import { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  useColorMode,
  Icon,
  Flex,
  Badge,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Button,
  Link,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../brandConfig";
import {
  FaServer,
  FaLock,
  FaBalanceScale,
  FaChartLine,
  FaUsers,
  FaShieldAlt,
  FaArrowRight,
  FaExchangeAlt,
  FaQuestionCircle,
} from "react-icons/fa";
import { GiTwoCoins, GiReceiveMoney, GiScales } from "react-icons/gi";
export const TokenomicsPage = () => {
  const { colorMode } = useColorMode();

  // Pendulum state for visualization
  const [pendulumPosition] = useState(50); // 0 = validators, 100 = delegators, 50 = balanced

  const feeDistribution = [
    { label: "Validators", percentage: 40, color: "#3b82f6", range: "30-50%" },
    { label: "Delegators", percentage: 30, color: "#64ffda", range: "20-40%" },
    { label: "Development", percentage: 20, color: "#8b5cf6", range: "Fixed" },
    { label: "Treasury", percentage: 10, color: "#f59e0b", range: "Fixed" },
  ];

  const triggerThresholds = [
    { metric: "Active Validators", threshold: "< 40 of 52", action: "Swing to Validators", ratio: "50% / 20%" },
    { metric: "Active Validators", threshold: "40-51 of 52", action: "Slight swing to Validators", ratio: "45% / 25%" },
    { metric: "Active Validators", threshold: "52 of 52", action: "Balanced", ratio: "40% / 30%" },
    { metric: "Relay Nodes", threshold: "< 50 globally", action: "Swing to Validators", ratio: "50% / 20%" },
    { metric: "Total Staked", threshold: "< 25% of supply", action: "Swing to Delegators", ratio: "30% / 40%" },
    { metric: "Total Staked", threshold: "25-40% of supply", action: "Slight swing to Delegators", ratio: "35% / 35%" },
    { metric: "Total Staked", threshold: "> 50% of supply", action: "Balanced", ratio: "40% / 30%" },
    { metric: "Stake Concentration", threshold: "Top 3 > 50%", action: "Swing to Delegators", ratio: "30% / 40%" },
  ];

  const faqs = [
    {
      question: "How do I earn B52 rewards?",
      answer: "You can earn by either running a validator node (requires 52,000 B52 and infrastructure) or by delegating your tokens to an existing validator (minimum 1,000 B52, no hardware needed). Both earn a share of network fees from all games."
    },
    {
      question: "What is the Incentive Pendulum?",
      answer: "The Incentive Pendulum automatically adjusts the reward split between validators and delegators based on network health. If we need more validators, rewards shift toward them. If we need more staked tokens for security, rewards shift toward delegators. This keeps the network balanced."
    },
    {
      question: "Why do Development and Treasury stay fixed at 20% and 10%?",
      answer: "Development funding ensures continuous improvement of the protocol, while the Treasury provides emergency reserves and funds community grants. These need to be stable and predictable, so they don't swing with the pendulum."
    },
    {
      question: "What triggers the pendulum to swing?",
      answer: "Network health metrics like: number of active validators (target: 52), number of relay nodes, total tokens staked, and stake distribution. When any metric is below target, the pendulum shifts rewards to attract what's needed."
    },
    {
      question: "If I delegate, do I risk losing my tokens?",
      answer: "Yes, partially. If your validator misbehaves (double-signing, extended downtime), both their stake and your delegation get slashed proportionally. This is by design - it incentivizes you to choose reputable validators and creates shared security responsibility."
    },
    {
      question: "What's the difference between validators and relay nodes?",
      answer: "Validators participate in consensus, sign blocks, and verify game settlements. They require 52,000 B52 stake. Relay nodes propagate transactions and game state globally, adding geographic redundancy and DDoS resistance. They don't require staking but help decentralize the infrastructure."
    },
    {
      question: "How does more staking make the network more secure?",
      answer: "To attack the network, you'd need to control 67% of staked tokens. The more tokens staked by honest participants, the more expensive an attack becomes. With 30M B52 staked, an attacker would need ~$20M+ to attempt an attack - and they'd lose it all when caught."
    },
    {
      question: "What happens to slashed tokens?",
      answer: "Slashed tokens are typically burned or sent to the community pool, permanently reducing supply. This makes the remaining tokens more scarce and adds a real cost to misbehavior."
    },
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box
        minH="100vh"
        bg="#0a0a0f"
        color={getColor("text.primary", colorMode)}
      >
        <Container maxW="1000px" py={20}>
          <VStack spacing={12} align="stretch">
            {/* Header */}
            <VStack spacing={6} textAlign="center">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                B52 TOKEN ECONOMICS
              </Badge>
              <Heading
                size="2xl"
                color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}
              >
                Earn by Contributing to the Network
              </Heading>
              <Text
                fontSize="xl"
                color={getColor("text.secondary", colorMode)}
                maxW="3xl"
              >
                B52 is the native token of Block52. You earn a share of every card game by
                providing security (running infrastructure) or stability (staking tokens).
              </Text>
            </VStack>

            {/* Token Stats */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Box
                p={6}
                bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.50"}
                borderRadius="lg"
                border="2px solid"
                borderColor="#3b82f6"
              >
                <Stat>
                  <StatLabel color={getColor("text.secondary", colorMode)}>Total Supply</StatLabel>
                  <StatNumber color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                    52 Million
                  </StatNumber>
                  <StatHelpText color={getColor("text.secondary", colorMode)}>Fixed forever</StatHelpText>
                </Stat>
              </Box>
              <Box
                p={6}
                bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.1)" : "teal.50"}
                borderRadius="lg"
                border="2px solid"
                borderColor="#64ffda"
              >
                <Stat>
                  <StatLabel color={getColor("text.secondary", colorMode)}>Max Validators</StatLabel>
                  <StatNumber color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                    52
                  </StatNumber>
                  <StatHelpText color={getColor("text.secondary", colorMode)}>One per card in the deck</StatHelpText>
                </Stat>
              </Box>
              <Box
                p={6}
                bg={colorMode === "dark" ? "rgba(139, 92, 246, 0.1)" : "purple.50"}
                borderRadius="lg"
                border="2px solid"
                borderColor="#8b5cf6"
              >
                <Stat>
                  <StatLabel color={getColor("text.secondary", colorMode)}>Security Fee</StatLabel>
                  <StatNumber color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                    1-3%
                  </StatNumber>
                  <StatHelpText color={getColor("text.secondary", colorMode)}>Of value secured</StatHelpText>
                </Stat>
              </Box>
            </SimpleGrid>

            <Divider borderColor={getColor("border.medium", colorMode)} />

            {/* Two Ways to Contribute */}
            <Box>
              <Heading size="lg" mb={6} color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                Two Ways to Contribute & Earn
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {/* Security Path */}
                <Box
                  p={6}
                  borderRadius="xl"
                  bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.50"}
                  border="2px solid"
                  borderColor="#3b82f6"
                >
                  <Flex align="center" mb={4}>
                    <Flex
                      w="56px"
                      h="56px"
                      borderRadius="lg"
                      bg="rgba(59, 130, 246, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                    >
                      <Icon as={FaServer} color="#3b82f6" boxSize={7} />
                    </Flex>
                    <Box>
                      <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                        Provide Security
                      </Heading>
                      <Text color="#3b82f6" fontSize="sm" fontWeight="medium">
                        Run a Validator or Relay Node
                      </Text>
                    </Box>
                  </Flex>
                  <VStack align="start" spacing={2} color={getColor("text.secondary", colorMode)} fontSize="sm">
                    <Text>• Infrastructure & decentralization</Text>
                    <Text>• Game verification & consensus</Text>
                    <Text>• Requires: 52,000 B52 + server</Text>
                    <Text>• Earns: 40% of fee pool (shared among 52)</Text>
                  </VStack>
                </Box>

                {/* Stability Path */}
                <Box
                  p={6}
                  borderRadius="xl"
                  bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.1)" : "teal.50"}
                  border="2px solid"
                  borderColor="#64ffda"
                >
                  <Flex align="center" mb={4}>
                    <Flex
                      w="56px"
                      h="56px"
                      borderRadius="lg"
                      bg="rgba(100, 255, 218, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                    >
                      <Icon as={FaLock} color="#64ffda" boxSize={7} />
                    </Flex>
                    <Box>
                      <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                        Provide Stability
                      </Heading>
                      <Text color="#64ffda" fontSize="sm" fontWeight="medium">
                        Delegate Your Tokens
                      </Text>
                    </Box>
                  </Flex>
                  <VStack align="start" spacing={2} color={getColor("text.secondary", colorMode)} fontSize="sm">
                    <Text>• Economic security & attack resistance</Text>
                    <Text>• Stake distribution</Text>
                    <Text>• Requires: 1,000+ B52 (no hardware)</Text>
                    <Text>• Earns: 30% of fee pool (proportional)</Text>
                  </VStack>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider borderColor={getColor("border.medium", colorMode)} />

            {/* Fee Distribution */}
            <Box>
              <Heading size="lg" mb={6} color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                Fee Distribution
              </Heading>
              <Text color={getColor("text.secondary", colorMode)} mb={6}>
                Every game generates a security fee (1-3% of value secured). Here's how it's distributed:
              </Text>

              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
                {feeDistribution.map((item, idx) => (
                  <Box
                    key={idx}
                    p={4}
                    borderRadius="lg"
                    bg={colorMode === "dark" ? `${item.color}15` : `${item.color}10`}
                    border="2px solid"
                    borderColor={item.color}
                    textAlign="center"
                  >
                    <Text
                      fontSize="3xl"
                      fontWeight="bold"
                      color={item.color}
                    >
                      {item.percentage}%
                    </Text>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}
                    >
                      {item.label}
                    </Text>
                    <Text fontSize="xs" color={getColor("text.secondary", colorMode)}>
                      Range: {item.range}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            <Divider borderColor={getColor("border.medium", colorMode)} />

            {/* The Incentive Pendulum */}
            <Box>
              <Flex align="center" mb={6}>
                <Icon as={GiScales} boxSize={8} color="#3b82f6" mr={3} />
                <Heading size="lg" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                  The Incentive Pendulum
                </Heading>
              </Flex>

              <Text color={getColor("text.secondary", colorMode)} mb={6}>
                The split between validators and delegators isn't fixed - it swings like a pendulum based on what
                the network needs. This keeps the network healthy and self-balancing.
              </Text>

              {/* Pendulum Visualization */}
              <Box
                p={6}
                borderRadius="xl"
                bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "gray.50"}
                border="1px solid"
                borderColor={colorMode === "dark" ? "rgba(59, 130, 246, 0.3)" : "gray.200"}
                mb={6}
              >
                <HStack justify="space-between" mb={4}>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold" color="#3b82f6">← INFRASTRUCTURE</Text>
                    <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>50% / 20%</Text>
                  </VStack>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>BALANCED</Text>
                    <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>40% / 30%</Text>
                  </VStack>
                  <VStack align="end" spacing={0}>
                    <Text fontWeight="bold" color="#64ffda">STAKERS →</Text>
                    <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>30% / 40%</Text>
                  </VStack>
                </HStack>

                {/* Progress bar as pendulum */}
                <Box position="relative" h="40px" mb={2}>
                  <Progress
                    value={pendulumPosition}
                    size="lg"
                    borderRadius="full"
                    bg={colorMode === "dark" ? "gray.700" : "gray.200"}
                    sx={{
                      "& > div": {
                        bgGradient: "linear(to-r, #3b82f6, #64ffda)",
                      }
                    }}
                  />
                  {/* Pendulum indicator */}
                  <Box
                    position="absolute"
                    top="50%"
                    left={`${pendulumPosition}%`}
                    transform="translate(-50%, -50%)"
                    w="24px"
                    h="24px"
                    borderRadius="full"
                    bg="white"
                    border="3px solid"
                    borderColor="#3b82f6"
                    boxShadow="0 2px 8px rgba(0,0,0,0.3)"
                  />
                </Box>
                <Text textAlign="center" fontSize="sm" color={getColor("text.secondary", colorMode)}>
                  Current: <Text as="span" fontWeight="bold" color="#64ffda">Balanced (40% Validators / 30% Delegators)</Text>
                </Text>
              </Box>

              {/* Why the pendulum */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
                <Box
                  p={4}
                  borderRadius="lg"
                  bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.50"}
                  border="1px solid"
                  borderColor="#3b82f6"
                >
                  <Heading size="sm" mb={2} color="#3b82f6">
                    If Not Enough Validators
                  </Heading>
                  <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                    Network less decentralized → Pendulum swings to validators → Higher rewards attract operators → More validators join
                  </Text>
                </Box>
                <Box
                  p={4}
                  borderRadius="lg"
                  bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.1)" : "teal.50"}
                  border="1px solid"
                  borderColor="#64ffda"
                >
                  <Heading size="sm" mb={2} color="#64ffda">
                    If Not Enough Stake
                  </Heading>
                  <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                    Network cheaper to attack → Pendulum swings to delegators → Higher rewards attract stakers → More tokens staked
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider borderColor={getColor("border.medium", colorMode)} />

            {/* Trigger Thresholds */}
            <Box>
              <Heading size="lg" mb={4} color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                Trigger Thresholds
              </Heading>
              <Text color={getColor("text.secondary", colorMode)} mb={6}>
                The pendulum adjusts automatically based on these network health metrics:
              </Text>

              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color={getColor("text.secondary", colorMode)}>Metric</Th>
                      <Th color={getColor("text.secondary", colorMode)}>Threshold</Th>
                      <Th color={getColor("text.secondary", colorMode)}>Pendulum Action</Th>
                      <Th color={getColor("text.secondary", colorMode)}>New Ratio (Val/Del)</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {triggerThresholds.map((row, idx) => (
                      <Tr key={idx}>
                        <Td color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"} fontWeight="medium">
                          {row.metric}
                        </Td>
                        <Td color={getColor("text.secondary", colorMode)}>{row.threshold}</Td>
                        <Td>
                          <Badge
                            colorScheme={row.action.includes("Validators") ? "blue" : row.action.includes("Delegators") ? "teal" : "gray"}
                            fontSize="xs"
                          >
                            {row.action}
                          </Badge>
                        </Td>
                        <Td color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"} fontWeight="bold">
                          {row.ratio}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>

            <Divider borderColor={getColor("border.medium", colorMode)} />

            {/* Earnings Example */}
            <Box>
              <Heading size="lg" mb={4} color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                Earnings Example
              </Heading>
              <Text color={getColor("text.secondary", colorMode)} mb={6}>
                What happens when a $10 pot is won on the network:
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box
                  p={5}
                  borderRadius="lg"
                  bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "white"}
                  border="1px solid"
                  borderColor={colorMode === "dark" ? "rgba(59, 130, 246, 0.3)" : "gray.200"}
                >
                  <Heading size="sm" mb={4} color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                    $10 Pot Breakdown
                  </Heading>
                  <VStack align="start" spacing={2} fontSize="sm">
                    <HStack justify="space-between" w="full">
                      <Text color={getColor("text.secondary", colorMode)}>Winner receives:</Text>
                      <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>$9.80</Text>
                    </HStack>
                    <HStack justify="space-between" w="full">
                      <Text color={getColor("text.secondary", colorMode)}>Security fee (2%):</Text>
                      <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>$0.20</Text>
                    </HStack>
                    <Divider my={2} />
                    <HStack justify="space-between" w="full">
                      <Text color="#3b82f6">→ Validators (40%):</Text>
                      <Text color="#3b82f6">$0.08</Text>
                    </HStack>
                    <HStack justify="space-between" w="full">
                      <Text color="#64ffda">→ Delegators (30%):</Text>
                      <Text color="#64ffda">$0.06</Text>
                    </HStack>
                    <HStack justify="space-between" w="full">
                      <Text color="#8b5cf6">→ Development (20%):</Text>
                      <Text color="#8b5cf6">$0.04</Text>
                    </HStack>
                    <HStack justify="space-between" w="full">
                      <Text color="#f59e0b">→ Treasury (10%):</Text>
                      <Text color="#f59e0b">$0.02</Text>
                    </HStack>
                  </VStack>
                </Box>

                <Box
                  p={5}
                  borderRadius="lg"
                  bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "white"}
                  border="1px solid"
                  borderColor={colorMode === "dark" ? "rgba(100, 255, 218, 0.3)" : "gray.200"}
                >
                  <Heading size="sm" mb={4} color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                    At Scale: $10M Monthly Volume
                  </Heading>
                  <VStack align="start" spacing={2} fontSize="sm">
                    <HStack justify="space-between" w="full">
                      <Text color={getColor("text.secondary", colorMode)}>Total pots:</Text>
                      <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>$10,000,000</Text>
                    </HStack>
                    <HStack justify="space-between" w="full">
                      <Text color={getColor("text.secondary", colorMode)}>Security fees (2%):</Text>
                      <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>$200,000</Text>
                    </HStack>
                    <Divider my={2} />
                    <HStack justify="space-between" w="full">
                      <Text color="#3b82f6">→ Validators (40%):</Text>
                      <Text color="#3b82f6" fontWeight="bold">$80,000</Text>
                    </HStack>
                    <HStack justify="space-between" w="full">
                      <Text color="#64ffda">→ Delegators (30%):</Text>
                      <Text color="#64ffda" fontWeight="bold">$60,000</Text>
                    </HStack>
                    <HStack justify="space-between" w="full">
                      <Text color={getColor("text.secondary", colorMode)} fontSize="xs" fontStyle="italic">
                        Per validator: ~$1,538/month
                      </Text>
                      <Text color={getColor("text.secondary", colorMode)} fontSize="xs" fontStyle="italic">
                        (1/52 share)
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider borderColor={getColor("border.medium", colorMode)} />

            {/* FAQ Section */}
            <Box>
              <Flex align="center" mb={6}>
                <Icon as={FaQuestionCircle} boxSize={6} color="#64ffda" mr={3} />
                <Heading size="lg" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                  Frequently Asked Questions
                </Heading>
              </Flex>

              <Accordion allowMultiple>
                {faqs.map((faq, idx) => (
                  <AccordionItem
                    key={idx}
                    border="1px solid"
                    borderColor={colorMode === "dark" ? "rgba(59, 130, 246, 0.2)" : "gray.200"}
                    borderRadius="lg"
                    mb={3}
                    overflow="hidden"
                  >
                    <AccordionButton
                      py={4}
                      _hover={{
                        bg: colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.50"
                      }}
                    >
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="medium" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                          {faq.question}
                        </Text>
                      </Box>
                      <AccordionIcon color="#3b82f6" />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <Text color={getColor("text.secondary", colorMode)} fontSize="sm" lineHeight="tall">
                        {faq.answer}
                      </Text>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </Box>

            <Divider borderColor={getColor("border.medium", colorMode)} />

            {/* Links Section */}
            <Box>
              <Heading size="lg" mb={6} color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                Learn More
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Button
                  as={RouterLink}
                  to="/timeline"
                  variant="outline"
                  size="lg"
                  borderColor="#3b82f6"
                  color="#3b82f6"
                  _hover={{ bg: "rgba(59, 130, 246, 0.1)" }}
                  leftIcon={<FaChartLine />}
                >
                  Development Timeline
                </Button>
                <Button
                  as={RouterLink}
                  to="/learn/what-is-b52-token"
                  variant="outline"
                  size="lg"
                  borderColor="#64ffda"
                  color="#64ffda"
                  _hover={{ bg: "rgba(100, 255, 218, 0.1)" }}
                  leftIcon={<GiTwoCoins />}
                >
                  B52 Token Overview
                </Button>
                <Button
                  as={RouterLink}
                  to="/learn/run-a-node"
                  variant="outline"
                  size="lg"
                  borderColor="#8b5cf6"
                  color="#8b5cf6"
                  _hover={{ bg: "rgba(139, 92, 246, 0.1)" }}
                  leftIcon={<FaServer />}
                >
                  Run a Node
                </Button>
              </SimpleGrid>
            </Box>

            {/* CTA */}
            <Box
              p={8}
              borderRadius="xl"
              bgGradient={colorMode === "dark"
                ? "linear(to-r, rgba(59, 130, 246, 0.2), rgba(100, 255, 218, 0.2))"
                : "linear(to-r, blue.50, teal.50)"}
              border="2px solid"
              borderColor="#3b82f6"
              textAlign="center"
            >
              <Heading size="lg" mb={4} color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                Ready to Earn?
              </Heading>
              <Text color={getColor("text.secondary", colorMode)} mb={6} maxW="2xl" mx="auto">
                Join the Block52 network as a validator or delegator and start earning from every game on the platform.
              </Text>
              <Button
                as={RouterLink}
                to="/token-sale"
                size="lg"
                px={8}
                py={6}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: "linear(to-r, #64ffda, #3b82f6)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 20px rgba(59, 130, 246, 0.4)",
                }}
                rightIcon={<FaArrowRight />}
                borderRadius="full"
                fontWeight="bold"
              >
                Get Started
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
    </>
  );
};

export default TokenomicsPage;
